import React from "react";
import {
  Switch,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

/**
 * Custom SwitchField component with enhanced functionality
 * Supports tooltips, validation, and custom styling
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Field label text
 * @param {string} props.name - Field name for form handling
 * @param {boolean} props.value - Current field value (true/false)
 * @param {Function} props.onChange - Change handler function
 * @param {string} props.helperText - Helper text to display in tooltip
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message string (from validation)
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {string} props.size - Switch size ('small', 'medium', 'large')
 * @param {string} props.color - Switch color ('primary', 'secondary', 'default', 'error', 'info', 'success', 'warning')
 * @param {Object} props.sx - Custom styling props
 */
const SwitchField = ({
  label,
  name,
  value = false,
  onChange,
  helperText,
  required = false,
  error = "",
  touched = false,
  disabled = false,
  size = "medium",
  color = "primary",
  sx = {},
  ...otherProps
}) => {
  /**
   * Renders the label with optional tooltip
   * If helperText is provided, shows an info icon with tooltip
   */
  const renderLabel = () => {
    if (!label) return null;

    return (
      <>
        {label}
        {helperText && (
          <Tooltip title={helperText} arrow>
            <IconButton size="small" sx={{ padding: 0, marginLeft: "4px" }}>
              <InfoIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  /**
   * Determine if field has error and should show error message
   * Error is shown only if field has been touched and has an error message
   */
  const hasError = touched && Boolean(error);
  const errorMessage = hasError ? error : "";

  /**
   * Handle switch change
   * Creates a proper event object for Formik
   */
  const handleSwitchChange = (event) => {
    if (onChange) {
      // Create a simple event object that Formik can handle
      const syntheticEvent = {
        target: {
          name: name,
          value: event.target.checked,
          type: "checkbox",
        },
      };
      onChange(syntheticEvent);
    }
  };

  return (
    <FormControl error={hasError} disabled={disabled} sx={sx}>
      <FormControlLabel
        control={
          <Switch
            name={name}
            checked={Boolean(value)}
            onChange={handleSwitchChange}
            size={size}
            color={color}
            disabled={disabled}
            {...otherProps}
          />
        }
        label={renderLabel()}
        required={required}
        sx={{
          "& .MuiFormControlLabel-asterisk": {
            color: "red",
            fontSize: "1.2rem",
          },
        }}
      />
      {hasError && <FormHelperText error>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default SwitchField;
